import { RiHealthBookLine } from 'react-icons/ri';
import {
  AButton,
  AInput,
  ASwitch,
  ATextArea,
  ATitle,
  ContainerForm,
  OptionFooter,
  PageIntro,
  PageLayout,
  PagePanel,
  PreviewForm,
} from '../../../components';
import { Col, Divider, Form, Row } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { PlusCircleOutlined } from '@ant-design/icons';
import { IInput, IResponseModelForms } from '../../../shared/interfaces';
import { useToast } from '../../../hooks/toast';
import InputCreateEdit from '../CreateEditInput';
import { schemaFormCreate, schemaFormEdit } from '../schemas/yup.form.schema';
import api from '../../../services/api';
import { useI18n } from '../../../shared/i18n';

const FormCreateEdit: React.FC = () => {
  const { id } = useParams();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [title, setTitle] = useState<string>();
  const [description, setDescription] = useState<string>();
  const [status, setStatus] = useState<boolean>(false);
  const [input, setInput] = useState<IInput | null>();
  const [inputs, setInputs] = useState<IInput[]>([]);
  const [modalCreate, setModalCreate] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(false);

  const onClickNewInput = useCallback(() => {
    setInput(null);
    setModalCreate(true);
  }, []);

  const renderModalInputCreateEdit = () => {
    if (modalCreate)
      return (
        <InputCreateEdit
          open={modalCreate}
          close={() => setModalCreate(false)}
          input={input}
          inputs={inputs}
          setInputs={setInputs}
          id={id}
        />
      );
  };

  const loadData = useCallback(async () => {
    interface IResponseForms {
      data: IResponseModelForms;
    }
    const response = await api.get(`/forms/${id}`);
    const { data }: IResponseForms = response;
    setTitle(data.form.title);
    setDescription(data.form.description);
    setInputs(data.inputs);
    setStatus(data.form.status ?? false);
  }, []);

  const handleEditSubmit = useCallback(() => {
    setDisabled(true);
    schemaFormEdit
      .validate({
        title,
        inputs,
        description: description ?? null,
        status,
      })
      .then(async (validated) => {
        await api
          .patch(`/forms/${id}`, validated)
          .then(() => {
            addToast({
              type: 'info',
              title: t('modelsFormsCreateEdit.toast.infoTitle'),
              description: t('modelsFormsCreateEdit.toast.updated'),
            });
          })
          .catch((error) => {
            const message = error.response.data.message;
            addToast({
              type: 'error',
              title: t('modelsFormsCreateEdit.toast.errorTitle'),
              description:
                message ?? t('modelsFormsCreateEdit.toast.editError'),
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('modelsFormsCreateEdit.toast.blankFields'),
          description: err.errors[0],
        });
      })
      .finally(() => {
        setDisabled(false);
      });
  }, [title, description, status, inputs]);

  const handleCreateSubmit = useCallback(() => {
    setDisabled(true);
    schemaFormCreate
      .validate({
        title,
        inputs,
        description: description ?? null,
      })
      .then(async (validated) => {
        await api
          .post('/forms', validated)
          .then(() => {
            addToast({
              type: 'success',
              title: t('modelsFormsCreateEdit.toast.successTitle'),
              description: t('modelsFormsCreateEdit.toast.created'),
            });
            setTimeout(function () {
              navigate(-1);
            }, 1100);
          })
          .catch((error) => {
            const message = error.response.data.message;
            addToast({
              type: 'error',
              title: t('modelsFormsCreateEdit.toast.errorTitle'),
              description:
                message ?? t('modelsFormsCreateEdit.toast.createError'),
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('modelsFormsCreateEdit.toast.blankFields'),
          description: err.errors[0],
        });
      })
      .finally(() => {
        setDisabled(false);
      });
  }, [title, description, inputs]);

  useEffect(() => {
    if (id !== 'new') {
      loadData();
    }
  }, [id]);

  return (
    <PageLayout>
      <PageIntro
        badge={t('modelsFormsCreateEdit.intro.badge')}
        icon={<RiHealthBookLine />}
        title={
          id === 'new'
            ? t('modelsFormsCreateEdit.intro.createTitle')
            : t('modelsFormsCreateEdit.intro.editTitle')
        }
        description={t('modelsFormsCreateEdit.intro.description')}
      />
      <PagePanel>
        <ContainerForm>
          <Form layout="vertical" style={{ width: '90%', marginTop: 20 }}>
            <Row>
              <Col span={24}>
                <Form.Item
                  label={t('modelsFormsCreateEdit.fields.title')}
                  required
                >
                  <AInput
                    value={title}
                    placeholder={t(
                      'modelsFormsCreateEdit.fields.titlePlaceholder',
                    )}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <Form.Item
                  label={t('modelsFormsCreateEdit.fields.description')}
                >
                  <ATextArea
                    value={description}
                    placeholder={t(
                      'modelsFormsCreateEdit.fields.descriptionPlaceholder',
                    )}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              {id !== 'new' && (
                <Form.Item label={t('modelsFormsCreateEdit.fields.active')}>
                  <ASwitch
                    checked={status}
                    checkedChildren={t('common.yes')}
                    unCheckedChildren={t('common.no')}
                    onChange={() => {
                      setStatus(!status);
                    }}
                  />
                </Form.Item>
              )}
            </Row>
            <Divider />
            <ATitle level={4}>
              {t('modelsFormsCreateEdit.fields.inputs')}
            </ATitle>
            <Row justify="end">
              <AButton
                type="default"
                onClick={onClickNewInput}
                icon={<PlusCircleOutlined />}
              >
                {t('modelsFormsCreateEdit.fields.addInput')}
              </AButton>
            </Row>
            <PreviewForm
              inputs={inputs}
              setInputs={setInputs}
              setInput={setInput}
              setModalCreate={setModalCreate}
            />
            <OptionFooter
              onClickCancel={() => navigate(-1)}
              disabled={disabled}
              onClickOk={id !== 'new' ? handleEditSubmit : handleCreateSubmit}
            />
          </Form>
        </ContainerForm>
      </PagePanel>
      {renderModalInputCreateEdit()}
    </PageLayout>
  );
};

export default FormCreateEdit;
